/* eslint-disable @typescript-eslint/no-this-alias */
import { SingleMetricContainerTemplateV2 } from "app/components/widget/dashboard/singleMetric/SingleMetricContainerTemplate-v2";
import _ from "lodash";
import type { IPptSingleMetricRequest, IPptSlideExportRequest } from "app/services/PptExportService/PptExportServiceTypes";
import {
    adaptSingleMetricChart,
    adaptSingleMetricRecords,
    resolveSingleMetricType,
} from "../widget-utilities/widgetPpt/PptSingleMetricWidgetUtils";
import { getWidgetSubtitle, getWidgetTitle } from "../widget-utilities/widgetPpt/PptWidgetUtils";
import { Widget } from "./Widget";
import SingleMetricService from "app/components/single-metric/SingleMetricService";
import type { IRowObject } from "../widget-config/metrics/@types/IObjects";

export class SingleMetricWidget extends Widget {
    public isPptSupported = () => {
        const singleMetricType = resolveSingleMetricType(this);
        return !!singleMetricType && !!this?.data;
    };

    public getDataForPpt = (): IPptSlideExportRequest => {
        const singleMetricType = resolveSingleMetricType(this);

        return {
            title: getWidgetTitle(this),
            subtitle: getWidgetSubtitle(this, { showEntities: true }),
            type: "single",
            details: {
                type: singleMetricType,
                data: adaptSingleMetricRecords(this.data, this.metadata.rows),
                chart: adaptSingleMetricChart(singleMetricType, this),
            } as IPptSingleMetricRequest,
        };
    };

    static getWidgetMetadataType() {
        return "SingleMetric";
    }

    static getWidgetResourceType() {
        return "SingleMetric";
    }

    protected setMetadata() {
        const rows: Record<string, any>[] = [];
        _.forEach(this._metricTypeConfig.objects, (object: IRowObject) => {
            rows.push(
                SingleMetricService.row({
                    field: object.name,
                    displayName: object.title,
                    cellTemplate: object.cellTemp,
                    headerTemplate: object.headTemp || this._metricTypeConfig.properties.headTemp,
                    format: object.format,
                    titleFormat: object.titleFormat,
                    rowClass: object.rowClass,
                    isBeta: object.isBeta,
                    ppt: object.ppt,
                    tooltip: object.tooltip,
                    filter: object.filter,
                    displayNoData: object.displayNoData,
                    invertChangeColor: object.invertChangeColor,
                }),
            );
        });
        this.metadata = { rows };
    }

    private static validateField(name: string, data: any): boolean {
        if (name === "Trend" && _.isArray(data)) {
            const trendOk = _.some(data, (val) => val !== -1);
            return trendOk;
        }
        return _.isNumber(data) || _.isString(data) || _.isObject(data);
    }

    protected validateData(response: any) {
        const that = this;
        if (_.isFunction(that._widgetConfig.properties.validateData)) {
            return that._widgetConfig.properties.validateData(response);
        } else {
            let dataObjects = _.head(_.values(response));
            if (_.isObject(dataObjects) && !_.isArray(dataObjects)) {
                dataObjects = [dataObjects];
            }
            return _.every(dataObjects, (dataObj) => {
                // validate fields
                const hasData = _.some<any>(this.metadata.rows, (row) =>
                    SingleMetricWidget.validateField(row.field, dataObj[row.field]),
                );
                return hasData;
            });
        }
    }

    callbackOnGetData(response: any) {
        // should be implemented on each of the extending classes
        this.data = response.Data[this._params.keys];
        //Merge response.Data with response.KeysDataVerification for isGAVerified flag per property.
        this.mergeGAVerifiedFlag(response);
    }

    onResize() {
        return;
    }

    get templateComponent() {
        return SingleMetricContainerTemplateV2;
    }
}

SingleMetricWidget.register();
